import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Typography } from '@mui/material';
import { OrderInputType, PatientFormData } from 'models/order-input/shared';
import PatientForm from 'components/order-flow/shared/patient-form.component';
import DentistsWithClinic from './dentist-with-clinic/dentists-with-clinic.component';
import InternalSection from './internal-section/internal-section.component';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import { hasRequiredPatientNames } from 'components/order-flow/shared/order-input-helper';

type PatientClinicStepProps = {
    onNextClick: () => void;
};
const PatientClinicStep: FC<PatientClinicStepProps> = ({ onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(LabOrderContext);
    const [isPatientFormValid, setIsPatientFormValid] = useState(order.inputType !== OrderInputType.NEW);
    const [panNumberEnabled, setPanNumberEnabled] = useState(false);

    const handlePatientChange = (formData: PatientFormData, isFormValid: boolean) => {
        setIsPatientFormValid(isFormValid);
        setOrder((prev) => ({
            ...prev,
            patient: {
                ...prev.patient,
                ...formData,
            },
        }));
    };

    const isNextStepDisabled = () => {
        return (
            !isPatientFormValid ||
            !hasRequiredPatientNames(order.patient) ||
            !order.customer ||
            !order.dentist ||
            !order.deliveryDetails.pickUpLocation?._id ||
            !order.deliveryDetails.destination?._id ||
            (panNumberEnabled && !order.panNumber)
        );
    };

    return (
        <Fragment>
            {order ? (
                <Fragment>
                    <Box>
                        <Box mb={2}>
                            {order.inputType === OrderInputType.NEW ? (
                                <Typography variant="h1">{translate('orders:REGISTER_NEW_ORDER')}</Typography>
                            ) : (
                                <Typography variant="h1">{translate('orders:UPDATE_ORDER')}</Typography>
                            )}
                        </Box>
                        <Box mb={2}>
                            <Divider />
                        </Box>

                        {order.orderForm ? (
                            <Box mb={2}>
                                <AiAlert
                                    title={translate('common:AI_ASSISTANT')}
                                    message={translate('order-forms:AI_ADDED_PATIENT_AND_CLINIC')}
                                />
                            </Box>
                        ) : undefined}

                        <Box mb={4}>
                            <Box mb={1}>
                                <Typography variant="h2">{translate('orders:PATIENT_INFORMATION')}</Typography>
                            </Box>
                            <PatientForm
                                isEdit={order.inputType !== OrderInputType.NEW}
                                patient={order.patient}
                                onPatientChange={handlePatientChange}
                            />
                        </Box>
                        <Box mb={4}>
                            <Box mb={2}>
                                <Typography variant="h2">{translate('orders:CLINIC_AND_DENTIST')}</Typography>
                            </Box>
                            <DentistsWithClinic />
                        </Box>
                        <Box mb={4}>
                            <Box mb={2}>
                                <Typography variant="h2">{translate('orders:INTERNAL_SECTION')}</Typography>
                            </Box>
                            <InternalSection onPanNumberEnabledChange={setPanNumberEnabled} />
                        </Box>

                        <Box display="flex" justifyContent="center">
                            <Button
                                variant="contained"
                                disabled={isNextStepDisabled()}
                                onClick={() => onNextClick()}
                                data-test="patient-lab-step.next"
                            >
                                {translate('common:NEXT_STEP')}
                            </Button>
                        </Box>
                    </Box>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default PatientClinicStep;
